<template>
  <header class="bg-white dark:bg-gray-900 shadow-sm sticky top-0 z-50">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="flex items-center justify-between h-16">
        <router-link data-testid="app-logo" to="/" class="flex items-center gap-2">
          <span class="text-2xl">🏥</span>
          <span class="text-elderly-lg font-bold text-indigo-600 dark:text-indigo-400">{{ $t('common.appName') }}</span>
        </router-link>

        <nav v-if="isAuthenticated" data-testid="navigation-menu" class="hidden md:flex items-center gap-1">
          <router-link
            v-for="link in navLinks"
            :key="link.to"
            :to="link.to"
            :data-testid="link.to === '/article' ? 'article-link' : undefined"
            class="btn-25d px-3 py-2 text-elderly-base font-medium text-gray-700 dark:text-gray-300 hover:text-indigo-600 dark:hover:text-indigo-400"
            active-class="bg-indigo-50 dark:bg-gray-800 text-indigo-600 dark:text-indigo-400"
          >
            {{ link.label }}
          </router-link>
        </nav>

        <div class="flex items-center gap-2">
          <div class="relative">
            <select
              v-model="currentLocale"
              :aria-label="t('a11y.selectLanguage')"
              class="appearance-none bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 pr-8 text-sm font-medium text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              @change="switchLocale"
            >
              <option v-for="lang in availableLanguages" :key="lang.value" :value="lang.value">{{ lang.label }}</option>
            </select>
          </div>

          <button
            class="btn-25d p-2 text-gray-600 dark:text-gray-400"
            :aria-label="t('a11y.toggleTheme')"
            @click="handleToggleTheme"
          >
            {{ isDark ? '☀️' : '🌙' }}
          </button>

          <div v-if="isAuthenticated" class="hidden md:flex items-center gap-2">
            <span class="text-sm text-gray-600 dark:text-gray-400">{{ userName }} <span class="text-xs text-gray-400">({{ userRole }})</span></span>
            <button
              data-testid="logout-button"
              class="btn-25d btn-25d-danger text-sm"
              @click="handleLogout"
            >
              {{ $t('common.logout') }}
            </button>
          </div>

          <button
            class="md:hidden btn-25d p-2 text-gray-600 dark:text-gray-400"
            aria-label="Toggle mobile menu"
            @click="toggleMobile"
          >
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path v-if="!mobileOpen" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
              <path v-else stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      <div v-if="mobileOpen && isAuthenticated" class="md:hidden pb-4 border-t border-gray-100 dark:border-gray-800">
        <nav class="flex flex-col gap-1 pt-2">
          <router-link
            v-for="link in navLinks"
            :key="link.to"
            :to="link.to"
            class="btn-25d px-3 py-3 text-elderly-base font-medium text-gray-700 dark:text-gray-300 text-left"
            active-class="bg-indigo-50 dark:bg-gray-800 text-indigo-600 dark:text-indigo-400"
            @click="mobileOpen = false"
          >
            {{ link.label }}
          </router-link>
          <button
            class="btn-25d btn-25d-danger px-3 py-3 text-elderly-base text-left"
            @click="handleLogout"
          >
            {{ $t('common.logout') }}
          </button>
        </nav>
      </div>
    </div>
  </header>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useTheme } from '@/composables/useTheme'
import { useDebounce } from '@/composables/useDebounce'
import { useLang } from '@/composables/useLang'
import { useUserStore } from '@/stores/user'
import { useRouter } from 'vue-router'

const { t } = useI18n()
const { theme, toggleTheme } = useTheme()
const { availableLanguages, currentLocale, setLocale } = useLang()
const userStore = useUserStore()
const router = useRouter()

const mobileOpen = ref(false)

const isAuthenticated = computed(() => userStore.isAuthenticated)
const userName = computed(() => userStore.user?.name || '')
const userRole = computed(() => {
  if (userStore.isElder) return t('login.elderRole')
  if (userStore.isFamily) return t('login.familyRole')
  return ''
})
const isDark = computed(() => theme.value === 'dark')

const navLinks = computed(() => [
  { to: '/dashboard', label: t('nav.dashboard') },
  { to: '/health', label: t('nav.health') },
  { to: '/medication', label: t('nav.medication') },
  { to: '/sos', label: t('nav.sos') },
  { to: '/family', label: t('nav.family') },
  { to: '/article', label: t('nav.article') },
])

const { debounced: debouncedToggleTheme } = useDebounce(toggleTheme, 300)
function handleToggleTheme() {
  debouncedToggleTheme()
}

const { debounced: debouncedLogout } = useDebounce(() => {
  userStore.logout()
  router.push('/login')
}, 300)
function handleLogout() {
  debouncedLogout()
}

function toggleMobile() {
  mobileOpen.value = !mobileOpen.value
}

const { debounced: debouncedSwitchLocale } = useDebounce(() => {
  setLocale(currentLocale.value)
}, 300)
function switchLocale() {
  debouncedSwitchLocale()
}
</script>
